import type { StatusLevel } from './StatusPill';

export type Annotation = {
  /** Unix seconds. */
  atSec: number;
  label: string;
  tone: StatusLevel;
};

export type AnnotationStripProps = {
  annotations: Annotation[];
  /** Operator pin action — typically wired to LinkedCursor → setT(now). */
  onPinMoment?: () => void;
  /** Optional: jump to a specific annotation time. */
  onSelect?: (atSec: number) => void;
};

const TONE_CHIP: Record<StatusLevel, string> = {
  critical: 'bg-critical-bg text-critical-fg border-critical',
  warn: 'bg-warn-bg text-warn-fg border-warn',
  watch: 'bg-watch-bg text-watch-fg border-watch',
  healthy: 'bg-healthy-bg text-healthy-fg border-healthy',
  info: 'bg-info-bg text-info-fg border-info',
};

/**
 * Annotations strip — pills marking auto-detected events ("BPFI
 * emerges", "RMS breach") + a "+ Pin moment" CTA the operator presses
 * to drop their own marker at the current cursor.
 *
 * Annotations are read-only here; the authoring surface is the `.`
 * keyboard shortcut wired in S5SignalAnalysis (pin current moment).
 * The strip is intentionally non-scrolling for now — the design
 * shows a small handful of detected events, not a dense timeline;
 * if real anomaly detection produces >5 in a window we'll wrap to a
 * second row before adding horizontal scroll.
 */
export function AnnotationStrip({
  annotations,
  onPinMoment,
  onSelect,
}: AnnotationStripProps) {
  return (
    <div
      role="group"
      aria-label="Annotations"
      className="flex flex-wrap items-center gap-sp-2"
    >
      <span className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
        Annotations
      </span>
      {annotations.length === 0 ? (
        <span className="text-fs-meta text-gray-700">
          None auto-detected in this window
        </span>
      ) : (
        annotations.map((a) => (
          <button
            key={`${a.label}-${a.atSec}`}
            type="button"
            onClick={onSelect ? () => onSelect(a.atSec) : undefined}
            title={new Date(a.atSec * 1000).toLocaleString()}
            className={
              'inline-flex items-center gap-sp-1 rounded-r-chip border px-sp-2 py-sp-1 font-mono text-fs-meta transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-info ' +
              TONE_CHIP[a.tone]
            }
            aria-label={`${a.label} at ${new Date(a.atSec * 1000).toLocaleString()}`}
          >
            {a.label}
          </button>
        ))
      )}
      {onPinMoment ? (
        <button
          type="button"
          onClick={onPinMoment}
          className="ml-sp-1 inline-flex items-center gap-sp-1 rounded-r-chip border border-dashed border-gray-500 px-sp-2 py-sp-1 text-fs-meta text-gray-900 hover:border-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
          title="Press . to pin the cursor"
        >
          + Pin moment <span className="font-mono text-gray-700">.</span>
        </button>
      ) : null}
    </div>
  );
}
